import React from 'react';
import { Crown, Headset } from 'lucide-react';
import Modal from './Modal';
import { useNavigate } from 'react-router-dom';

interface SubscriptionModalProps {
  isOpen: boolean;
  onUpgrade?: () => void;
  onPrimaryAction?: () => void;
  title?: string;
  message?: string;
  feature?: string;
  showClose?: boolean;
  onClose?: () => void;
  chatbotId: string;
  primaryActionLabel?: string;
  primaryActionType?: 'upgrade' | 'contact-support';
}

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onUpgrade,
  onPrimaryAction,
  title = 'Upgrade Required',
  message = 'This feature is not available on your current plan.',
  feature,
  showClose = false,
  onClose,
  chatbotId,
  primaryActionLabel = 'Upgrade Now',
  primaryActionType = 'upgrade',
}) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose?.();
    if (onUpgrade) {
      onUpgrade();
    } else {
      setTimeout(() => {
        navigate(`/chatbot-builder/${chatbotId}/plans`);
      }, 0);
    }
  };

  const handlePrimary = () => {
    if (onPrimaryAction) {
      onPrimaryAction();
      return;
    }

    handleUpgrade();
  };

  // Provide a no-op when showClose is false so Modal always gets a valid onClose
  const handleClose = showClose && onClose ? onClose : () => { };
  const ActionIcon = primaryActionType === 'contact-support' ? Headset : Crown;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      description={message}
      icon={Crown}
      iconVariant="amber"
      size="sm"
      closeOnBackdropClick={showClose}
      showFooter
      actions={[
        {
          label: 'Cancel',
          color: 'secondary',
          outline: true,
          onClick: handleClose,
        },
        {
          label: (
            <span className="flex items-center gap-2">
              <ActionIcon className="w-4 h-4" />
              {primaryActionLabel}
            </span>
          ),
          color: 'warning',
          onClick: handlePrimary,
        },
      ]}
    >

      {feature && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-center">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
            Feature: {feature}
          </p>
        </div>
      )}
    </Modal>
  );
};

export default SubscriptionModal;
